import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [editFormData, setEditFormData] = useState({
        Name: '', Email: '', ContactNumber: '', Address: '',
        DOB: '', District: '', State: '', Pincode: ''
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '', newPassword: '', confirmPassword: ''
    });

    const [selectedImage, setSelectedImage] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    const navigate = useNavigate();
    // IMPORTANT: Change this to match your server route
    const API_BASE_URL = 'https://theagencybazaar.com/api/Register';

    useEffect(() => {
        const init = async () => {
            const userData = localStorage.getItem('user');
            const isAuthenticated = localStorage.getItem('isAuthenticated');

            if (!userData || !isAuthenticated) {
                navigate('/login');
                return;
            }

            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
                setEditFormData({
                    Name: parsedUser.Name || '',
                    Email: parsedUser.Email || '',
                    ContactNumber: parsedUser.ContactNumber || '',
                    Address: parsedUser.Address || '',
                    DOB: parsedUser.DOB ? parsedUser.DOB.split('T')[0] : '',
                    District: parsedUser.District || '',
                    State: parsedUser.State || '',
                    Pincode: parsedUser.Pincode || ''
                });
            } catch (error) {
                console.error('Error parsing user data:', error);
                localStorage.removeItem('user');
                localStorage.removeItem('isAuthenticated');
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };
        
        init();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
        navigate('/login');
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleInputChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showMessage('danger', 'Image size should be less than 5MB');
                return;
            }
            if (!file.type.startsWith('image/')) {
                showMessage('danger', 'Please select an image file');
                return;
            }
            setSelectedImage(file);
        }
    };

    // ==================== UPDATE PROFILE ====================
    const handleUpdateProfile = async () => {
        setUpdating(true);
        setMessage({ type: '', text: '' });

        try {
            if (!editFormData.Name || !editFormData.Email || !editFormData.ContactNumber) {
                showMessage('danger', 'Name, Email, and Contact Number are required');
                setUpdating(false);
                return;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(editFormData.Email)) {
                showMessage('danger', 'Please enter a valid email address');
                setUpdating(false);
                return;
            }

            const phoneRegex = /^[0-9]{10}$/;
            if (!phoneRegex.test(editFormData.ContactNumber)) {
                showMessage('danger', 'Please enter a valid 10-digit contact number');
                setUpdating(false);
                return;
            }

            const updateData = {
                ContactID: user.ContactID,
                Name: editFormData.Name.trim(),
                Email: editFormData.Email.trim(),
                ContactNumber: editFormData.ContactNumber.trim(),
                Address: editFormData.Address.trim() || null,
                DOB: editFormData.DOB || null,
                District: editFormData.District.trim() || null,
                State: editFormData.State.trim() || null,
                Pincode: editFormData.Pincode.trim() || null
            };

            console.log('Sending update request to:', `${API_BASE_URL}/update-profile`);
            console.log('Update data:', updateData);

            const response = await fetch(`${API_BASE_URL}/update-profile`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(updateData)
            });

            console.log('Response status:', response.status);
            
            if (!response.ok) {
                const text = await response.text();
                console.log('Response text:', text);
                throw new Error(`HTTP ${response.status}: ${text.substring(0, 100)}`);
            }
            
            const result = await response.json();
            console.log('Update response:', result);

            if (result.success) {
                const updatedUser = { ...user, ...result.user };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setUser(updatedUser);
                setShowEditModal(false);
                showMessage('success', 'Profile updated successfully!');
                
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                showMessage('danger', result.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Update Profile Error:', error);
            showMessage('danger', `Error: ${error.message}. Make sure your backend server is running on https://theagencybazaar.com`);
        } finally {
            setUpdating(false);
        }
    };

    // ==================== UPDATE PHOTO ====================
    const handlePhotoUpdate = async () => {
        if (!selectedImage) {
            showMessage('warning', 'Please select an image first');
            return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append('profileImage', selectedImage);
        formData.append('ContactID', user.ContactID);

        try {
            console.log('Sending photo update to:', `${API_BASE_URL}/update-photo`);
            
            const response = await fetch(`${API_BASE_URL}/update-photo`, {
                method: 'POST',
                body: formData
            });

            console.log('Photo response status:', response.status);
            
            if (!response.ok) {
                const text = await response.text();
                console.log('Response text:', text);
                throw new Error(`HTTP ${response.status}: ${text.substring(0, 100)}`);
            }

            const result = await response.json();

            if (result.success) {
                const updatedUser = { ...user, ImagePath: result.imagePath };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                setUser(updatedUser);
                setShowPhotoModal(false);
                setSelectedImage(null);
                showMessage('success', 'Profile photo updated successfully!');
                setTimeout(() => window.location.reload(), 1500);
            } else {
                showMessage('danger', result.message || 'Failed to update photo');
            }
        } catch (error) {
            console.error('Photo Update Error:', error);
            showMessage('danger', `Error: ${error.message}. Check if server is running.`);
        } finally {
            setUploading(false);
        }
    };

    // ==================== CHANGE PASSWORD ====================
    const handleChangePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return showMessage('danger', 'New passwords do not match');
        }
        if (passwordData.newPassword.length < 6) {
            return showMessage('danger', 'Password must be at least 6 characters long');
        }

        try {
            console.log('Sending password change to:', `${API_BASE_URL}/change-password`);
            
            const response = await fetch(`${API_BASE_URL}/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ContactID: user.ContactID,
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                })
            });

            console.log('Password response status:', response.status);
            
            if (!response.ok) {
                const text = await response.text();
                console.log('Response text:', text);
                throw new Error(`HTTP ${response.status}: ${text.substring(0, 100)}`);
            }

            const result = await response.json();

            if (result.success) {
                setShowChangePasswordModal(false);
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                showMessage('success', 'Password changed successfully!');
            } else {
                showMessage('danger', result.message || 'Error changing password');
            }
        } catch (error) {
            console.error('Change Password Error:', error);
            showMessage('danger', `Error: ${error.message}`);
        }
    };

    // ==================== RENDER UI ====================
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-primary bg-gradient">
                <div className="text-center text-white">
                    <div className="spinner-border text-light mb-3" style={{ width: '4rem', height: '4rem' }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h4>Loading Dashboard...</h4>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-primary bg-gradient">
                <div className="text-center text-white">
                    <h3>No user data found</h3>
                    <button className="btn btn-light btn-lg mt-4" onClick={() => navigate('/login')}>
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 position-relative">
            {/* Message Toast */}
            {message.text && (
                <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 9999 }}>
                    <div className={`alert alert-${message.type} alert-dismissible fade show shadow`} role="alert">
                        <i className={`bi bi-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                        {message.text}
                        <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
                    </div>
                </div>
            )}

            {/* Gradient Background */}
            <div className="position-fixed top-0 start-0 end-0" 
                 style={{ height: '300px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', zIndex: -1 }}></div>

            <div className="container-fluid px-0">
                {/* Navbar */}
                <nav className="navbar navbar-expand-lg navbar-dark shadow" style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}>
                    <div className="container">
                        <a className="navbar-brand d-flex align-items-center fw-bold fs-4" href="#">
                            <i className="bi bi-person-badge fs-2 me-2"></i> User Dashboard
                        </a>
                        <div className="d-flex align-items-center">
                            <span className="text-white me-4 d-none d-md-block">
                                Welcome, <strong>{user.Name}</strong>
                            </span>
                            <button className="btn btn-outline-light" onClick={handleLogout}>
                                <i className="bi bi-box-arrow-right me-2"></i>Logout
                            </button>
                        </div>
                    </div>
                </nav>

                <div className="container py-5">
                    {/* Welcome Banner */}
                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="card border-0 shadow-lg overflow-hidden">
                                <div className="row g-0">
                                    <div className="col-lg-8 p-4 p-md-5 d-flex flex-column justify-content-center">
                                        <h1 className="display-5 fw-bold text-primary">Welcome back, {user.Name}!</h1>
                                        <p className="lead text-muted">Here's your personalized dashboard.</p>
                                    </div>
                                    <div className="col-lg-4 bg-primary text-white d-flex align-items-center justify-content-center p-5">
                                        <div className="text-center">
                                            <i className="bi bi-person-circle display-1"></i>
                                            <p className="mt-3 mb-1 fw-bold">Active Status</p>
                                            <span className="badge bg-success fs-6">✓ Online</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* Profile Card */}
                        <div className="col-lg-4">
                            <div className="card border-0 shadow h-100">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0"><i className="bi bi-person me-2"></i>Profile Overview</h5>
                                </div>
                                <div className="card-body text-center py-4">
                                    {user.ImagePath ? (
                                        <img
                                            src={`https://theagencybazaar.com${user.ImagePath}`}
                                            alt="Profile"
                                            className="rounded-circle border border-5 border-white shadow mb-4"
                                            style={{ width: '180px', height: '180px', objectFit: 'cover' }}
                                            onError={(e) => { 
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                                const parent = e.target.parentElement;
                                                if (parent) {
                                                    parent.innerHTML = '<div class="rounded-circle border border-5 border-white bg-light d-inline-flex align-items-center justify-content-center mb-4 shadow" style="width: 180px; height: 180px;"><i class="bi bi-person-circle text-primary" style="font-size: 100px;"></i></div>';
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div className="rounded-circle border border-5 border-white bg-light d-inline-flex align-items-center justify-content-center mb-4 shadow" 
                                             style={{ width: '180px', height: '180px' }}>
                                            <i className="bi bi-person-circle text-primary" style={{ fontSize: '100px' }}></i>
                                        </div>
                                    )}
                                    <h3 className="fw-bold">{user.Name}</h3>
                                    <p className="text-muted"><i className="bi bi-envelope me-2"></i>{user.Email}</p>
                                    <button className="btn btn-primary w-100 mt-3" onClick={() => setShowEditModal(true)}>
                                        <i className="bi bi-pencil-square me-2"></i>Edit Profile
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Contact Info + Quick Actions */}
                        <div className="col-lg-8">
                            {/* Contact Information Card */}
                            <div className="card border-0 shadow mb-4">
                                <div className="card-header bg-info text-white">
                                    <h5 className="mb-0"><i className="bi bi-telephone me-2"></i>Contact Information</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="border rounded p-3 h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                        <i className="bi bi-telephone-fill"></i>
                                                    </div>
                                                    <h6 className="mb-0">Contact Number</h6>
                                                </div>
                                                <p className="fs-5 fw-bold mb-0">{user.ContactNumber}</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="border rounded p-3 h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                        <i className="bi bi-calendar3"></i>
                                                    </div>
                                                    <h6 className="mb-0">Date of Birth</h6>
                                                </div>
                                                <p className="fs-5 fw-bold mb-0">
                                                    {user.DOB ? new Date(user.DOB).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not specified'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="border rounded p-3 h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                        <i className="bi bi-geo-alt-fill"></i>
                                                    </div>
                                                    <h6 className="mb-0">Address</h6>
                                                </div>
                                                <p className="mb-0">{user.Address || 'Not specified'}</p>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="border rounded p-3 h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-danger text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                        <i className="bi bi-pin-map-fill"></i>
                                                    </div>
                                                    <h6 className="mb-0">Location</h6>
                                                </div>
                                                <div>
                                                    {user.District && <div><strong>District:</strong> {user.District}</div>}
                                                    {user.State && <div><strong>State:</strong> {user.State}</div>}
                                                    {user.Pincode && <div><strong>Pincode:</strong> {user.Pincode}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Account Details & Quick Actions */}
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div className="card border-0 shadow h-100">
                                        <div className="card-header bg-secondary text-white">
                                            <h5 className="mb-0"><i className="bi bi-info-circle me-2"></i>Account Details</h5>
                                        </div>
                                        <div className="card-body">
                                            <table className="table table-borderless mb-0">
                                                <tbody>
                                                    <tr>
                                                        <th className="text-muted"><i className="bi bi-envelope text-primary me-2"></i>Email:</th>
                                                        <td>{user.Email}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted"><i className="bi bi-calendar-check text-success me-2"></i>Member Since:</th>
                                                        <td>{user.CreatedAt ? new Date(user.CreatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted"><i className="bi bi-shield-check text-info me-2"></i>Status:</th>
                                                        <td><span className="badge bg-success">Active</span></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="card border-0 shadow h-100">
                                        <div className="card-header bg-dark text-white">
                                            <h5 className="mb-0"><i className="bi bi-lightning-charge me-2"></i>Quick Actions</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row g-3 text-center">
                                                <div className="col-6">
                                                    <button className="btn btn-outline-primary w-100 py-3" onClick={() => setShowEditModal(true)}>
                                                        <i className="bi bi-pencil-square fs-4 d-block mb-1"></i>Edit Profile
                                                    </button>
                                                </div>
                                                <div className="col-6">
                                                    <button className="btn btn-outline-success w-100 py-3" onClick={() => setShowChangePasswordModal(true)}>
                                                        <i className="bi bi-shield-lock fs-4 d-block mb-1"></i>Change Password
                                                    </button>
                                                </div>
                                                <div className="col-6">
                                                    <button className="btn btn-outline-info w-100 py-3" onClick={() => setShowPhotoModal(true)}>
                                                        <i className="bi bi-image fs-4 d-block mb-1"></i>Update Photo
                                                    </button>
                                                </div>
                                                <div className="col-6">
                                                    <Link to="/GetApplyLeads" className="btn btn-outline-warning w-100 py-3">
                                                        <i className="bi bi-people fs-4 d-block mb-1"></i>View Leads
                                                    </Link>
                                                </div>
                                                {/* NEW BUTTON: Admin Messages */}
                                                <div className="col-12 mt-2">
                                                    <Link to="/admin/messages" className="btn btn-danger w-100 py-3">
                                                        <i className="bi bi-envelope-paper fs-4 d-block mb-1"></i>Admin Messages
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="bg-dark text-white py-5 mt-5">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <h5>User Dashboard System</h5>
                                <p className="text-muted mb-0">© {new Date().getFullYear()} All rights reserved.</p>
                            </div>
                            <div className="col-md-6 text-md-end">
                                <a href="#" className="text-white mx-2"><i className="bi bi-facebook fs-4"></i></a>
                                <a href="#" className="text-white mx-2"><i className="bi bi-twitter fs-4"></i></a>
                                <a href="#" className="text-white mx-2"><i className="bi bi-linkedin fs-4"></i></a>
                                <a href="#" className="text-white mx-2"><i className="bi bi-instagram fs-4"></i></a>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>

            {/* EDIT PROFILE MODAL */}
            {showEditModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title"><i className="bi bi-pencil-square me-2"></i>Edit Profile</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Full Name *</label>
                                        <input type="text" className="form-control" name="Name" value={editFormData.Name} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Email *</label>
                                        <input type="email" className="form-control" name="Email" value={editFormData.Email} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Contact Number *</label>
                                        <input type="text" className="form-control" name="ContactNumber" value={editFormData.ContactNumber} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Date of Birth</label>
                                        <input type="date" className="form-control" name="DOB" value={editFormData.DOB} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Address</label>
                                        <textarea className="form-control" name="Address" rows="2" value={editFormData.Address} onChange={handleInputChange}></textarea>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">District</label>
                                        <input type="text" className="form-control" name="District" value={editFormData.District} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">State</label>
                                        <input type="text" className="form-control" name="State" value={editFormData.State} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Pincode</label>
                                        <input type="text" className="form-control" name="Pincode" value={editFormData.Pincode} onChange={handleInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleUpdateProfile} disabled={updating}>
                                    {updating ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Updating...</>
                                    ) : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* PHOTO UPDATE MODAL */}
            {showPhotoModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title"><i className="bi bi-image me-2"></i>Update Profile Photo</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPhotoModal(false)}></button>
                            </div>
                            <div className="modal-body text-center">
                                <div className="mb-4">
                                    {selectedImage ? (
                                        <img src={URL.createObjectURL(selectedImage)} alt="Preview" 
                                             className="rounded-circle border border-4 border-primary shadow" 
                                             style={{ width: '200px', height: '200px', objectFit: 'cover' }} />
                                    ) : user.ImagePath ? (
                                        <img src={`https://theagencybazaar.com${user.ImagePath}`} alt="Current" 
                                             className="rounded-circle border border-4 border-primary shadow" 
                                             style={{ width: '200px', height: '200px', objectFit: 'cover' }} />
                                    ) : (
                                        <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center border border-4 border-primary" 
                                             style={{ width: '200px', height: '200px' }}>
                                            <i className="bi bi-person-circle text-primary" style={{ fontSize: '100px' }}></i>
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Select New Photo</label>
                                    <input type="file" className="form-control" accept="image/*" onChange={handleImageSelect} />
                                    <small className="text-muted">Max size: 5MB. Supported: JPG, PNG, GIF</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowPhotoModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handlePhotoUpdate} disabled={uploading}>
                                    {uploading ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Uploading...</>
                                    ) : 'Update Photo'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CHANGE PASSWORD MODAL */}
            {showChangePasswordModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header bg-success text-white">
                                <h5 className="modal-title"><i className="bi bi-shield-lock me-2"></i>Change Password</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowChangePasswordModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Current Password</label>
                                    <input type="password" className="form-control" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">New Password</label>
                                    <input type="password" className="form-control" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} />
                                    <small className="text-muted">At least 6 characters</small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Confirm New Password</label>
                                    <input type="password" className="form-control" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowChangePasswordModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-success" onClick={handleChangePassword}>Change Password</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;


